// Panel foydalanuvchilari, rollar va huquqlar — `xsib-identity` bilan
// haqiqiy kontrakt.
//
// Manba (KODDAN o'qilgan, taxmin emas):
//   · `user/resource/UserResource.java`
//   · `role/resource/RoleResource.java`
//   · `user/dto/UserView.java`, `role/dto/RoleView.java`,
//     `role/dto/RoleAssignment.java`, `role/dto/PermissionInfo.java`
//
// ⚠️ YO'LLAR HUJJATDAGIDAN FARQ QILADI — quyidagilar KODDA BOR:
//   GET    /api/users                   SAHIFALANGAN ro'yxat (2026-08-20)
//   GET    /api/users/{id}
//   POST   /api/users                   {username, rawPassword, displayName}
//   PUT    /api/users/{id}/password     {password}
//   PUT    /api/users/{id}/state        {state}        — HISOB (barcha tashkilot)
//   PUT    /api/users/{id}/membership   {state}        — JORIY tashkilotdagi a'zolik
//   PUT    /api/users/{id}/party-node   {partyNodeId}  — TUZILMAGA bog'lash
//   POST   /api/users/{id}/totp/reset
//   POST   /api/users/{id}/logout-all
//   POST   /api/users/{id}/roles        {roleId, scopeType, scopeId}   ← rol BERISH
//   DELETE /api/users/{id}/roles        {roleId, scopeType, scopeId}   ← rol OLIB TASHLASH
//   GET    /api/roles
//   GET    /api/roles/permissions       huquqlar katalogi
//
// ⚠️ SERVERDA BOR, LEKIN BU YERDAN CHAQIRILMAYDI: `PUT /partner` —
//   sherikning TURI va kassa bayrog'i Agentlar sahifasida beriladi.
//   `PUT /party-node` esa endi CHAQIRILADI (2026-08-22, egasi qarori):
//   har hisob — xodim ham, sherik ham — tashkiliy tuzilmaning bir
//   birligiga biriktiriladi va bu yagona joyda ko'rinadi.
//
// YO'Q ENDPOINTLAR (va shuning uchun panel ularni O'ZI hisoblaydi):
//   · `GET /api/users/{id}/roles`        — rollar `UserView.roles` ichida keladi
//   · `GET /api/users/{id}/permissions`  — hisoblangan huquqlar backendda
//     CHIQARILMAGAN. Panel ularni `UserView.roles` × `GET /api/roles` dagi
//     `permissions` birlashmasi sifatida hisoblaydi — backend `AccessGuard`
//     ham AYNAN shu birlashmani ishlatadi (`UserRoleRepository.permissionsOf`),
//     ya'ni natija bir xil. Bu TAXMIN emas: rol → huquq bog'lanishi yagona
//     manbadan (`role_permission`) keladi.
//   · foydalanuvchi QAYSI tashkilotlarga a'zo ekanini beradigan endpoint —
//     `user_tenant` RLS ostida va faqat JORIY tenant ko'rinadi
//     (`UserServiceImpl.listUsers` javadoc'i). Shuning uchun panel a'zolikni
//     faqat joriy tashkilot uchun ko'rsatadi va shuni AYTADI.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import {
  fetchAllRows,
  listParams,
  serverPageSchema,
  toPageResult,
  type SortFieldMap,
} from '@/shared/api/serverPage'
import { SCOPE_TYPES, isPermission, type Permission, type ScopeType } from '@/shared/auth/permissions'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

/** `UserState` — HISOB holati, barcha tashkilotlar uchun umumiy */
export const USER_STATES = ['ACTIVE', 'BLOCKED'] as const
export type UserState = (typeof USER_STATES)[number]

/** `MembershipState` — faqat JORIY tashkilotdagi a'zolik */
export const MEMBERSHIP_STATES = ['ACTIVE', 'BLOCKED'] as const
export type MembershipState = (typeof MEMBERSHIP_STATES)[number]

/**
 * `UserKind` — hisob SINFI (ADR-029), rol EMAS.
 *
 * ⚠️ Xodimning huquqlari `identity.role` dan DINAMIK keladi, sherikniki
 * esa backend kodida qotirilgan (`PartnerKind`). Shuning uchun panel
 * sherik uchun huquq ro'yxatini HISOBLAMAYDI — u yerda hisoblaydigan
 * narsa yo'q va taxmin qilingan ro'yxat ikkinchi haqiqat manbai
 * bo'lardi.
 */
export const USER_KINDS = ['STAFF', 'PARTNER'] as const
export type UserKind = (typeof USER_KINDS)[number]

const roleAssignmentSchema = z.object({
  roleId: uuidSchema,
  roleCode: z.string(),
  scopeType: z.enum(SCOPE_TYPES),
  /** `SUBTREE` uchun reseller id, `VENUE` uchun ob'ekt id; qolganida `null` */
  scopeId: uuidSchema.nullish(),
})

const userViewSchema = z.object({
  id: uuidSchema,
  username: z.string(),
  displayName: z.string().nullish(),
  state: z.enum(USER_STATES),
  membershipState: z.enum(MEMBERSHIP_STATES),
  createdAt: instantSchema.nullish(),
  lastLoginAt: instantSchema.nullish(),
  roles: z.array(roleAssignmentSchema).default([]),
  /**
   * ⚠️ Backend `default-property-inclusion: non_null` bilan ishlaydi, ya'ni
   * bog'lanish yo'q bo'lsa maydon javobda UMUMAN bo'lmaydi — `null` emas.
   * Shuning uchun `nullish()`, `nullable()` emas.
   */
  partyNodeId: uuidSchema.nullish(),
  /**
   * ⚠️ `nullish()` va sukut `STAFF`: ADR-029 dan oldingi javoblarda bu
   * maydon umuman bo'lmagan. Sxema uni majburiy qilsa, eskiroq backend
   * bilan butun ro'yxat parse xatosiga uchrardi — ya'ni bitta yangi
   * ustun butun sahifani o'chirib qo'yardi.
   */
  kind: z.enum(USER_KINDS).nullish(),
})

export interface RoleAssignment {
  roleId: string
  roleCode: string
  scopeType: ScopeType
  scopeId: string | null
}

export interface PanelUser {
  id: string
  username: string
  /** Bo'sh bo'lsa login ko'rsatiladi — jadvalda bo'sh katak qolmasin */
  displayName: string
  state: UserState
  membershipState: MembershipState
  createdAt: string | null
  /** `null` = hech qachon kirmagan (yangi hisob yoki hisob ishlatilmayapti) */
  lastLoginAt: string | null
  roles: readonly RoleAssignment[]
  /**
   * Bu hisob tashkiliy tuzilmaning qaysi BIRLIGIGA biriktirilgan
   * (`platform.org_node.id`); `null` — biriktirilmagan.
   *
   * ⚠️ `identity.app_user.id` EMAS — boshqa id fazosi. Chalkashtirish
   * xato BERMAYDI, shunchaki doira hech narsa topmaydi.
   *
   * Bog'lanmagan hisob hech kimning abonentini ko'rmaydi va bu ham
   * xato bermaydi — shuning uchun qiymat ekranda ochiq turadi.
   */
  partyNodeId: string | null
  /**
   * Hisob sinfi (ADR-029). `PARTNER` bo'lsa `roles` HAR DOIM bo'sh:
   * sherik huquqlari serverda KODDA, `identity.role` da emas.
   *
   * ⚠️ Sherik turi (`partnerKind`) va kassa bayrog'i bu yerda ATAYLAB
   * yo'q: ular Agentlar sahifasida boshqariladi va foydalanuvchilar
   * sahifasida ikkinchi haqiqat manbai bo'lardi.
   */
  kind: UserKind
}

function toPanelUser(raw: z.infer<typeof userViewSchema>): PanelUser {
  return {
    id: raw.id,
    username: raw.username,
    displayName: raw.displayName ?? raw.username,
    state: raw.state,
    membershipState: raw.membershipState,
    createdAt: raw.createdAt ?? null,
    lastLoginAt: raw.lastLoginAt ?? null,
    roles: raw.roles.map((r) => ({
      roleId: r.roleId,
      roleCode: r.roleCode,
      scopeType: r.scopeType,
      scopeId: r.scopeId ?? null,
    })),
    partyNodeId: raw.partyNodeId ?? null,
    kind: raw.kind ?? 'STAFF',
  }
}

/** Sherikmi — bitta joyda, chunki savol uch ekranda takrorlanadi */
export function isPartner(user: PanelUser): boolean {
  return user.kind === 'PARTNER'
}

/**
 * Ustun kaliti → `GET /api/users` ning `sortField` enumi.
 *
 * ⚠️ Enum YOPIQ: ro'yxatda yo'q qiymat serverda **400** beradi. Shuning
 * uchun xarita bitta joyda va sahifada takrorlanmaydi — ikkinchi nusxa
 * jimgina ajralib, butun jadvalni xatoga aylantirardi.
 */
const USER_SORT_FIELDS: SortFieldMap = {
  username: 'USERNAME',
  displayName: 'DISPLAY_NAME',
  state: 'STATE',
  lastLogin: 'LAST_LOGIN_AT',
  createdAt: 'CREATED_AT',
}

async function loadUserPage(
  params: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ rows: PanelUser[]; total: number }> {
  const response = await api.get<unknown>('/api/users', {
    params,
    ...(signal ? { signal } : {}),
  })
  const parsed = serverPageSchema(userViewSchema).parse(response.data)
  return { rows: parsed.rows.map(toPanelUser), total: parsed.total }
}

/**
 * Bir SAHIFA xodim — qidiruv, saralash va sahifalash SERVERDA.
 *
 * ⚠️ Qidiruv parametri `search` (`/api/agents` dagi `q` EMAS).
 */
export async function fetchUserPage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<PanelUser>> {
  const page = await loadUserPage(listParams(state, { sortFields: USER_SORT_FIELDS }), signal)
  return toPageResult(page, page.rows)
}

/**
 * BUTUN ro'yxat — «kim bu amalni qildi» ma'lumotnomasi uchun
 * (`useActorDirectory`), jadval uchun EMAS.
 */
export async function fetchAllUsers(): Promise<PanelUser[]> {
  return fetchAllRows((page, size) => loadUserPage({ page, size }))
}

/**
 * Bitta xodim — ro'yxat yonidagi kartochka uchun.
 *
 * ⚠️ Ro'yxatdan izlash endi YETARLI EMAS: ro'yxat sahifalangan va
 * tanlangan odam boshqa sahifada qolishi mumkin. Kartochka o'sha
 * paytda eskirgan nusxani ko'rsatardi — bloklangan hisob "faol"
 * bo'lib turardi.
 */
export async function fetchUser(id: string): Promise<PanelUser> {
  const response = await api.get<unknown>(`/api/users/${id}`)
  return toPanelUser(userViewSchema.parse(response.data))
}

export interface UserDraft {
  username: string
  rawPassword: string
  displayName?: string
}

/**
 * Yangi XODIM hisobi.
 *
 * ⚠️ Parol MINIMUM 12 belgi (`CreateUserCommand`) — bu abonent paroli emas,
 * internetga ochiq admin API'ni himoya qiladigan parol.
 *
 * ⚠️ Yaratilgan xodimda hali ROL YO'Q: u kira oladi, lekin hech narsa
 * ko'rmaydi. Rol alohida beriladi (`assignRole`) — shuning uchun UI
 * yaratgandan keyin darhol rol berishga undaydi.
 *
 * ⚠️ `kind` ANIQ yuboriladi. Server uni yubormaganda ham `STAFF` deb
 * oladi (`PartnerProfile.of`), lekin bu yerda sukutga tayanish forma
 * qaysi sinfni yaratayotganini kodda ko'rinmas qilardi — sherik
 * hisoblari boshqa oqimdan (Agentlar sahifasi) yaratiladi.
 */
export async function createUser(draft: UserDraft): Promise<PanelUser> {
  const response = await api.post<unknown>('/api/users', {
    username: draft.username,
    rawPassword: draft.rawPassword,
    displayName: draft.displayName ?? null,
    kind: 'STAFF',
  })
  return toPanelUser(userViewSchema.parse(response.data))
}

/**
 * Parolni O'RNATADI. O'qish yo'li ATAYLAB yo'q — backendda ham
 * (`PasswordRequest` javadoc'i, CLAUDE.md §7). Panel hech qachon
 * parolni ko'rsatmaydi va qaytarmaydi.
 */
export async function setUserPassword(id: string, password: string): Promise<void> {
  await api.put(`/api/users/${id}/password`, { password })
}

/**
 * O'Z parolini almashtirish — BUG-046.
 *
 * ⚠️ `id` YO'Q va bu ataylab: server foydalanuvchini FAQAT tokendan
 * oladi (`SelfPasswordResource` javadoc'i). Panel identifikator
 * yuborsa, u shunchaki e'tiborsiz qolardi — ya'ni maydonning o'zi
 * yolg'on va'da bo'lardi.
 *
 * ⚠️ Bu `setUserPassword` ning o'rnini bosmaydi: u administratorning
 * BOSHQA odam uchun amali (`user.update` huquqi bilan), bu esa har
 * qanday tirik sessiyaning o'z huquqi.
 *
 * ⚠️ Joriy parol xato bo'lsa server `401 BAD_CREDENTIALS` beradi.
 * Bu sessiyani YOPMAYDI: `client.ts` interceptori 401 ni faqat
 * `tag === 'UNAUTHORIZED'` yoki token muddati o'tganda sessiya deb
 * biladi (`looksLikeExpiredSession`) — tekshirildi, taxmin
 * qilinmadi.
 */
export async function changeOwnPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await api.post('/api/auth/password', { currentPassword, newPassword })
}

/** HISOB holati — odamni BARCHA tashkilotlarda to'sadi va joriy tokenlarini o'ldiradi */
export async function setUserState(id: string, state: UserState): Promise<void> {
  await api.put(`/api/users/${id}/state`, { state })
}

/** A'zolik holati — faqat JORIY tashkilotdagi kirishni yopadi/ochadi */
export async function setMembershipState(id: string, state: MembershipState): Promise<void> {
  await api.put(`/api/users/${id}/membership`, { state })
}

/**
 * Hisobni tashkiliy tuzilmaning bir BIRLIGIGA biriktiradi —
 * `PUT /api/users/{id}/party-node`.
 *
 * ⚠️ `PUT` bo'lsa ham bu TO'LIQ ALMASHTIRISH TUZOG'I EMAS: serverdagi
 * `PartyNodeRequest` da BITTA maydon bor (`partyNodeId`) va boshqa
 * hech narsa bu yo'ldan o'zgarmaydi. Staging'da o'lchandi (2026-08-22):
 * bog'lashdan oldin va keyin `GET /api/users/{id}` javobi maydonma-maydon
 * solishtirildi — `roles`, `state`, `membershipState`, `kind`, `cashDesk`
 * va qolgani DAXLSIZ qoldi, faqat `partyNodeId` o'zgardi.
 *
 * ⚠️ `null` — HAQIQIY qiymat: u bog'lanishni UZADI. Serverda `@NotNull`
 * ataylab yo'q va alohida `DELETE` ham yo'q — bitta fakt bitta yo'l bilan
 * o'zgaradi, ya'ni audit yozuvi ham bitta. Shuning uchun bu funksiya
 * `undefined` ni QABUL QILMAYDI: "yubormaslik" bilan "uzish" ni
 * chalkashtirish jimgina bog'lanishni o'chirardi.
 *
 * ⚠️ BIRLIK MAVJUDLIGI SERVERDA TEKSHIRILMAYDI (`UserServiceImpl`
 * javadoc'i): u boshqa sxemada va tekshiruv modullararo chaqiruv
 * bo'lardi. Ya'ni noto'g'ri id **200** qaytaradi va odam hech narsa
 * ko'rmaydigan bo'lib qoladi — panel shuning uchun faqat MAVJUD
 * ro'yxatdan tanlatadi, qo'lda UUID kiritishga yo'l qo'ymaydi.
 *
 * ⚠️ Huquq `user.role.assign` (`user.update` EMAS): bu amal odamning
 * NIMA KO'RISHINI o'zgartiradi.
 *
 * ⚠️ O'ZINI biriktirish serverda **403 `PRIVILEGE_ESCALATION`** bilan
 * to'siladi (o'lchangan) — chaqiruvchi bu variantni umuman
 * taklif qilmasligi kerak.
 *
 * Javob TANASIZ (`XsibResponse<Void>`) — `zod` sxemasi YO'Q, xuddi
 * qo'shni `setUserState`/`setMembershipState` kabi: tekshiradigan
 * ma'lumot yo'q, sxema esa `undefined` ni "tasdiqlab" yolg'on
 * ishonch berardi.
 */
export async function setUserPartyNode(id: string, partyNodeId: string | null): Promise<void> {
  await api.put(`/api/users/${id}/party-node`, { partyNodeId })
}

/**
 * Odamni JORIY tashkilotdan butunlay chiqaradi.
 *
 * ⚠️ HISOBNI O'CHIRMAYDI. Serverda `identity.app_user` qatori QOLADI —
 * `platform.audit_log.actor_id` FK'siz xom matn, ya'ni hisobni o'chirish
 * jurnaldagi "kim qildi" javobini hal qilib bo'lmaydigan qilib qo'yardi.
 * Yo'qoladigan narsa: shu tashkilotdagi a'zolik, rol grantlari va joriy
 * sessiyalar. Tasdiqlash oynasi AYNAN shuni yozadi.
 *
 * ⚠️ `setMembershipState(id, 'BLOCKED')` dan farqi — u vaqtinchalik
 * (odam ro'yxatda qoladi, bir bosishda qaytariladi), bu esa yakuniy:
 * rol grantlari yo'qoladi va qayta tiklash "yangidan berish" bo'ladi.
 */
export async function removeUserFromTenant(id: string): Promise<void> {
  await api.delete(`/api/users/${id}`)
}

/** TOTP holatini tozalaydi — keyingi kirishda `TOTP_SETUP` oqimi qaytadan boshlanadi */
export async function resetUserTotp(id: string): Promise<void> {
  await api.post(`/api/users/${id}/totp/reset`)
}

/** Barcha qurilmalardan majburiy chiqarish. Hisobni YOPMAYDI — odam qaytadan kira oladi */
export async function logoutAllDevices(id: string): Promise<void> {
  await api.post(`/api/users/${id}/logout-all`)
}

export interface RoleGrant {
  roleId: string
  scopeType: ScopeType
  /** `SUBTREE`/`VENUE` da MAJBURIY, qolganida `null` BO'LISHI SHART */
  scopeId: string | null
}

/**
 * ⚠️ `scopeId` qoidasi backend `Scope` rekordida QAT'IY: `SUBTREE`/`VENUE`
 * uchun majburiy, qolgan turlarda esa `null` bo'lishi SHART — aks holda
 * "scopeId ma'nosiz" xatosi qaytadi. Shuning uchun bu yerda ortiqchasi
 * ANIQ tozalanadi, forma eski qiymatni saqlab qolgan bo'lsa ham.
 */
function normalizeGrant(grant: RoleGrant) {
  const needsTarget = grant.scopeType === 'SUBTREE' || grant.scopeType === 'VENUE'
  return {
    roleId: grant.roleId,
    scopeType: grant.scopeType,
    scopeId: needsTarget ? grant.scopeId : null,
  }
}

export async function assignRole(userId: string, grant: RoleGrant): Promise<void> {
  await api.post(`/api/users/${userId}/roles`, normalizeGrant(grant))
}

/**
 * ⚠️ `DELETE` TANA bilan ketadi (`RoleGrantRequest`) — axios'da bu faqat
 * `{ data: ... }` orqali. Oddiy ikkinchi argument sifatida berilsa u
 * konfiguratsiya deb qabul qilinadi va so'rov TANASIZ ketardi: server
 * 400 qaytarib, sabab UI'da tushunarsiz ko'rinardi.
 */
export async function revokeRole(userId: string, grant: RoleGrant): Promise<void> {
  await api.delete(`/api/users/${userId}/roles`, { data: normalizeGrant(grant) })
}

// --- Rollar va huquqlar katalogi --------------------------------------------

const roleViewSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  system: z.boolean(),
  defaultScope: z.enum(SCOPE_TYPES),
  /** ⚠️ Huquqlar KOD bo'lib keladi (`Permission.code()` da `@JsonValue`) */
  permissions: z.array(z.string()).default([]),
  userCount: z.number().default(0),
})

export interface Role {
  id: string
  code: string
  name: string
  /** Tizim roli — huquqlari o'zgartirilmaydi (nusxa olib tahrirlanadi) */
  system: boolean
  defaultScope: ScopeType
  /** Faqat panel katalogida bor kodlar. Notanishi e'tiborsiz — panel yiqilmasin */
  permissions: readonly Permission[]
  /** Panel bilmaydigan, lekin serverda bor kodlar — ular ham KO'RSATILADI */
  unknownPermissions: readonly string[]
  userCount: number
}

function toRole(raw: z.infer<typeof roleViewSchema>): Role {
  const known: Permission[] = []
  const unknown: string[] = []
  for (const code of raw.permissions) {
    if (isPermission(code)) known.push(code)
    else unknown.push(code)
  }
  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    system: raw.system,
    defaultScope: raw.defaultScope,
    permissions: known,
    unknownPermissions: unknown,
    userCount: raw.userCount,
  }
}

/**
 * Ustun kaliti → `GET /api/roles` ning `sortField` enumi.
 *
 * ⚠️ ESKI SUKUT TARTIB YO'Q. Ilgari server `is_system, code` bo'yicha
 * qaytarardi, ya'ni tizim rollari DOIM tepada turardi. Endi sukut
 * `CREATED_AT DESC` — ro'yxatda tizim va o'z rollari aralash keladi.
 * Ajratish uchun `Tizim` yorlig'i va ustun bo'yicha saralash bor.
 */
const ROLE_SORT_FIELDS: SortFieldMap = {
  code: 'CODE',
  name: 'NAME',
  system: 'IS_SYSTEM',
  createdAt: 'CREATED_AT',
}

async function loadRolePage(
  params: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ rows: Role[]; total: number }> {
  const response = await api.get<unknown>('/api/roles', {
    params,
    ...(signal ? { signal } : {}),
  })
  const parsed = serverPageSchema(roleViewSchema).parse(response.data)
  return { rows: parsed.rows.map(toRole), total: parsed.total }
}

/** Bir SAHIFA rol — qidiruv, saralash va sahifalash SERVERDA */
export async function fetchRolePage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<Role>> {
  const page = await loadRolePage(listParams(state, { sortFields: ROLE_SORT_FIELDS }), signal)
  return toPageResult(page, page.rows)
}

/**
 * BUTUN katalog — rol BERISH tanlagichi uchun (`UsersPage`), jadval
 * uchun EMAS. Tanlagichda bir sahifa ko'rsatish operatorga mavjud
 * rolni "yo'q" deb ko'rsatardi.
 */
export async function fetchAllRoles(): Promise<Role[]> {
  return fetchAllRows((page, size) => loadRolePage({ page, size }))
}

export interface RoleDraft {
  code: string
  name: string
  defaultScope: ScopeType
  permissions: readonly string[]
}

/**
 * Yangi rol — `POST /api/roles`.
 *
 * ⚠️ `code` naqshi backend bilan BIR XIL: `^[A-Z][A-Z0-9_]{1,31}$`
 * (`CreateRoleCommand` javadoc'i). Formada shu naqsh oldindan
 * tekshiriladi, chunki serverning javobi operator uchun kech keladi.
 *
 * ⚠️ O'ZIDA BO'LMAGAN HUQUQNI BERIB BO'LMAYDI (self-escalation bloki,
 * `RoleServiceImpl#requireNoEscalation`). Buni server 403 bilan rad
 * etadi — panel esa bunday huquqni umuman belgilashga qo'ymaydi.
 */
export async function createRole(draft: RoleDraft): Promise<Role> {
  const response = await api.post<unknown>('/api/roles', draft)
  return toRole(roleViewSchema.parse(response.data))
}

/**
 * Rol huquqlarini TO'LIQ almashtiradi — `PUT /api/roles/{id}/permissions`.
 *
 * ⚠️ Bu qo'shish emas, ALMASHTIRISH: yuborilmagan huquq OLIB TASHLANADI.
 * Shuning uchun chaqiruvchi rolning butun to'plamini yuborishi shart,
 * shu jumladan panel katalogida yo'q (`unknownPermissions`) kodlarni ham —
 * aks holda ular jimgina yo'qolardi.
 *
 * ⚠️ TIZIM ROLLARI o'zgartirilmaydi (server 409/500 bilan rad etadi va
 * "nusxa olib tahrirlang" deydi). Panel ularga tahrirlash tugmasini
 * ko'rsatmaydi, o'rniga "nusxa olish" beradi.
 */
export async function setRolePermissions(
  roleId: string,
  permissions: readonly string[],
): Promise<Role> {
  const response = await api.put<unknown>(`/api/roles/${roleId}/permissions`, { permissions })
  return toRole(roleViewSchema.parse(response.data))
}

/**
 * Rolni o'chiradi — `DELETE /api/roles/{id}` (D2).
 *
 * ⚠️ Server FAQAT ikki shart birga bajarilganda o'chiradi:
 * rol TIZIM roli EMAS (`is_system`) va unda BIRORTA ega YO'Q. Ikkala
 * holatda ham javob `409` va tushunarli matn bilan keladi — panel
 * uni o'zi takrorlamaydi, chunki "kimda bor" savoliga oxirgi javobni
 * SERVER beradi (`RoleService#deleteRole` javadoc'i: sanoq bilan
 * o'chirish orasida poyga oynasi bor va uni baza yopadi).
 *
 * ⚠️ Javob TANASIZ (`204`) — `zod` sxemasi YO'Q va bo'lishi ham
 * kerak emas.
 */
export async function deleteRole(roleId: string): Promise<void> {
  await api.delete(`/api/roles/${roleId}`)
}

const permissionInfoSchema = z.object({
  /** Enum konstantasi nomi (`SUBSCRIBER_READ`) — ko'rsatilmaydi, kod ishlatiladi */
  name: z.string(),
  code: z.string(),
  /** XAVFLI huquq (`Permission.DANGEROUS`) — UI'da alohida belgilanadi */
  dangerous: z.boolean(),
})

export interface PermissionInfo {
  code: string
  dangerous: boolean
}

/**
 * Huquqlar katalogi — `GET /api/roles/permissions`.
 *
 * ⚠️ `role.read` huquqini talab qiladi, ya'ni faqat `user.read` ga ega
 * operator uni ololmaydi. Chaqiruvchi buni `enabled` bilan boshqaradi va
 * katalog bo'lmaganda "xavfli" belgisi shunchaki chizilmaydi — sahifa
 * ishlashda davom etadi.
 */
export async function fetchPermissionCatalog(): Promise<PermissionInfo[]> {
  const response = await api.get<unknown>('/api/roles/permissions')
  return z
    .array(permissionInfoSchema)
    .parse(response.data)
    .map((raw) => ({ code: raw.code, dangerous: raw.dangerous }))
}

// --- Doira nishoni (scopeId) uchun ro'yxatlar --------------------------------
//
// `SUBTREE` va `VENUE` doiralari NISHON talab qiladi. Operator UUID ni
// qo'lda yozmasligi kerak, shuning uchun ro'yxat beriladi. So'rov xato
// bersa (masalan operatorda `venue.read` yo'q) chaqiruvchi qo'lda kiritish
// maydoniga o'tadi — bu yerdan faqat ma'lumot keladi.

const scopeTargetRowSchema = z.object({
  id: uuidSchema,
  code: z.string().nullish(),
  name: z.string().nullish(),
  displayName: z.string().nullish(),
})

const scopeTargetSchema = z.array(scopeTargetRowSchema)

export interface ScopeTarget {
  id: string
  label: string
}

function toScopeTargets(raw: z.infer<typeof scopeTargetSchema>): ScopeTarget[] {
  return raw.map((row) => ({
    id: row.id,
    label: [row.name ?? row.displayName ?? row.code ?? row.id, row.code]
      .filter((part, index, all) => part && all.indexOf(part) === index)
      .join(' · '),
  }))
}

/**
 * `VENUE` doirasi uchun ob'ektlar.
 *
 * ⚠️ `GET /api/venues` endi SAHIFALANGAN. Bu tanlagich, ya'ni bir
 * sahifa YETMAYDI: ro'yxatda bo'lmagan ob'ekt operatorga "yo'q" bo'lib
 * ko'rinardi va u doirani noto'g'ri qo'yardi.
 */
export async function fetchVenueTargets(): Promise<ScopeTarget[]> {
  const rows = await fetchAllRows(async (page, size) => {
    const response = await api.get<unknown>('/api/venues', { params: { page, size } })
    return serverPageSchema(scopeTargetRowSchema).parse(response.data)
  })
  return toScopeTargets(rows)
}

/**
 * Bitta tugun — JORIY bog'lanishning nomini ko'rsatish uchun.
 *
 * Ro'yxatdan qidirish yetarli emas: tugun doira tashqarisida yoki
 * ro'yxat chegarasidan (500 qator) tashqarida bo'lishi mumkin. U holda
 * kartochkada UUID emas, hech bo'lmasa aniq "noma'lum" holati chiqadi —
 * chaqiruvchi `null` ni shunday talqin qiladi.
 */
export async function fetchPartyNodeTarget(id: string): Promise<ScopeTarget | null> {
  try {
    const response = await api.get<unknown>(`/api/agents/${id}`)
    const parsed = scopeTargetSchema.parse([response.data])
    return toScopeTargets(parsed)[0] ?? null
  } catch {
    return null
  }
}
